import { type BaseQueryFn, createApi, type FetchArgs, fetchBaseQuery, type FetchBaseQueryError } from "@reduxjs/toolkit/query/react";

import { Languages } from "src/translations";
import { getAccessToken, getAssignedDataGroup, getRefreshToken, setAccessToken, setRefreshToken } from "../router/auth";
import { authActions } from "../router/auth/actions";
import { isUserSessionActive } from "../router/auth/isUserSessionActive";
import { getIdToken, setIdToken } from "../router/auth/SecurityManager";
import { msalInstance } from "../router/config/msalConfig";
import { ResourceName } from "../router/config/ResourceName";
import { routes } from "../router/config/routes";
import { getBeBaseUrl } from "../setup/config/utils";
import type { CredentialsDto } from "../types/be/generated";
import { HTTP_HEADERS } from "./HttpHeaders";

// eslint-disable-next-line no-underscore-dangle
const isMSEntraLogin = window.__RUNTIME_CONFIG__?.IDENTITY_SERVICE === "entra";

const baseQuery = fetchBaseQuery({
    baseUrl: getBeBaseUrl(),
    prepareHeaders: async (headers) => {
        let token: string | null;

        if (isMSEntraLogin) {
            try {
                const account = msalInstance.getAllAccounts()[0];
                if (account) {
                    const tokenResponse = await msalInstance.acquireTokenSilent({
                        account,
                        scopes: ["user.read"],
                    });
                    setAccessToken(tokenResponse.accessToken);
                    setIdToken(tokenResponse.idToken);
                }
            } catch {}
            token = getIdToken();
        } else {
            token = getAccessToken();
        }

        const dataGroup = getAssignedDataGroup();

        if (token) {
            headers.set(HTTP_HEADERS.AUTHORIZATION, `Bearer ${token}`);
        }

        if (dataGroup) {
            headers.set(HTTP_HEADERS.DATA_GROUP, `${dataGroup.code}`);
        }

        headers.set(HTTP_HEADERS.ACCEPT_LANGUAGE, `${Languages.en}`);

        return headers;
    },
});

const baseQueryWithReAuth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {
    let result = await baseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 401 && !isMSEntraLogin) {
        // try to refresh the token
        const fetchArgs = {
            url: "/renew",
            method: "POST",
            body: { refreshToken: getRefreshToken() },
        };

        const refreshResult = await baseQuery(fetchArgs, api, extraOptions);

        if (refreshResult.data) {
            const response = refreshResult.data as CredentialsDto;
            if (response.jwt && response.refreshToken) {
                // store the new token
                setAccessToken(response.jwt);
                setRefreshToken(response.refreshToken);
                // retry the initial query
                result = await baseQuery(args, api, extraOptions);
            }
        }

        // If it still results in 401, redirect to the unauthorized route
        if (result.error && result.error.status === 401) {
            api.dispatch(authActions.redirect(routes.unauthorised.path));
        }
    }

    if (result.error && result.error.status === 500 && !isMSEntraLogin) {
        // try to get a new token
        const fetchArgs = {
            url: "/renew",
            method: "POST",
            body: { refreshToken: getRefreshToken() },
        };

        const refreshResult = await baseQuery(fetchArgs, api, extraOptions);
        if (refreshResult.data) {
            const response = refreshResult.data as CredentialsDto;
            if (response.jwt && response.refreshToken) {
                // store the new token
                setAccessToken(response.jwt);
                setRefreshToken(response.refreshToken);
                // retry the initial query
                result = await baseQuery(args, api, extraOptions);
            }
        } else {
            api.dispatch(authActions.redirect(routes.login.path));
        }
    }
    if (!isMSEntraLogin && !isUserSessionActive()) {
        if (result) {
            const fetchArgs = {
                url: "/renew",
                method: "POST",
                body: { refreshToken: getRefreshToken() },
            };

            const refreshResult = await baseQuery(fetchArgs, api, extraOptions);
            if (refreshResult.data) {
                const response = refreshResult.data as CredentialsDto;
                if (response.jwt && response.refreshToken) {
                    // store the new token
                    setAccessToken(response.jwt);
                    setRefreshToken(response.refreshToken);
                    // retry the initial query
                    result = await baseQuery(args, api, extraOptions);
                }
            } else {
                api.dispatch(authActions.redirect(routes.login.path));
            }
        }
    }

    return result;
};

export const baseApi = createApi({
    baseQuery,
    endpoints: () => ({}),
    reducerPath: "api",
});

export const baseAuthApi = createApi({
    baseQuery: baseQueryWithReAuth,
    endpoints: () => ({}),
    reducerPath: "authApi",
    refetchOnMountOrArgChange: true,
    tagTypes: ["mainCache", "formCache", "formConfigCache", ...Object.keys(ResourceName)],
});
